import { Box, Button, Grid, styled } from '@mui/material'
import Close from '@mui/icons-material/Close'
import Backspace from '@mui/icons-material/Backspace'
import { get, isNumber } from 'lodash-es'
import {
    IMapKeyCodeToID,
    KeyCodes,
    MapKeyCodeToID,
} from 'components/KeyCode/KeyCode'
import { useScreenSize } from 'hooks/useScreenSize/useScreenSize'
import { CalculatorSwitchMode } from 'components/Sell/CalculationInput/CalculatePanel'
import { useCallback, useEffect, useMemo } from 'react'
import nameof from 'utilities/nameof'
import useKeyCode from 'components/KeyCode/useKeyCode'

export const NumpadButton = styled(Button)(({ theme }) => ({
    borderRadius: 0,
    width: '100%',
    fontSize: 16,
    margin: 1,
    boxShadow: 'none',
    border:
        theme.palette.mode === 'light'
            ? 'none'
            : '1px rgba(255, 255, 255, 0.5) solid',
    color: theme.palette.default.contrastText,
    backgroundColor: theme.palette.default.main,
    '&:hover': {
        background: theme.palette.primary.main,
        color: 'white',
    },
    [theme.breakpoints.down('sm')]: {
        minHeight: 72,
    },
    [theme.breakpoints.up('sm')]: {
        fontSize: 24,
        margin: 4,
        minHeight: 75,
        '@media (max-height: 820px)': {
            fontSize: 24,
            minHeight: 68,
            margin: 4,
        },
        '@media (max-height: 730px)': {
            fontSize: 22,
            minHeight: 54,
            margin: 3,
        },
        '@media (max-height: 680px)': {
            fontSize: 22,
            minHeight: 44,
            margin: 2,
        },
    },
}))

export type NumberPadActionValue =
    | '' // no action
    | '5%'
    | '10%'
    | '15%'
    | '20%'
    | 'CLEAR'
    | 'BACK'
    | 'PLUS'
    | 'MINUS'
    | 'EQUAL'
    | 'DOT'
    | '00'

interface IPanelBtnProps {
    label: number | string
    value?: number | NumberPadActionValue
    onClick?: () => void
    icon?: any
    id: number | string
    disabled: boolean
    isMobile: boolean
}

interface NumberPadProps {
    onClick: (value: number | NumberPadActionValue, isPreset?: boolean) => void
    hideQuickButtons?: boolean
    hideDot?: boolean
    isCalculatorMode?: boolean
    onSwitchMode?: (mode: CalculatorSwitchMode) => void
    currentSwitchMode?: CalculatorSwitchMode
    disabled?: boolean
    disableKeyboard?: boolean
    isDoubleZeroEnabled?: boolean
    disableSwitchMode?: boolean
}

const MAP_KEYCODE_TO_ID = nameof<IMapKeyCodeToID>()

const PanelBtn = (props: IPanelBtnProps) => {
    const { label, icon, onClick, id, disabled } = props
    return (
        <NumpadButton
            onClick={onClick}
            id={get(MapKeyCodeToID, id)}
            disabled={disabled}
        >
            <Box display="flex" flexDirection="column" alignItems="center">
                {icon}
                {label.toString()}
            </Box>
        </NumpadButton>
    )
}

function NumberPad({
    onClick,
    hideQuickButtons = false,
    hideDot = false,
    isCalculatorMode = false,
    onSwitchMode,
    currentSwitchMode = CalculatorSwitchMode.Percentage,
    disabled = false,
    disableKeyboard = false,
    isDoubleZeroEnabled = false,
    disableSwitchMode = false,
}: NumberPadProps) {
    const { isMobile } = useScreenSize()
    const { hoverKeyPress } = useKeyCode()
    const numbers = [1, 2, 3, 4, 5, 6, 7, 8, 9]

    const isPercentage = useMemo(
        () => currentSwitchMode === CalculatorSwitchMode.Percentage,
        [currentSwitchMode],
    )

    const handleKeyPress = useCallback(
        (event: any) => {
            const { key, keyCode } = event
            if (KeyCodes.delete?.some((i) => i.code === keyCode)) {
                onClick('CLEAR') // delete
                hoverKeyPress(MAP_KEYCODE_TO_ID.delete)
            } else if (KeyCodes.backspace?.some((i) => i.code === keyCode)) {
                onClick('BACK') // backspace
                hoverKeyPress(MAP_KEYCODE_TO_ID.back)
            } else if (KeyCodes.dot?.some((i) => i.code === keyCode)) {
                onClick('DOT') // .
                hoverKeyPress(MAP_KEYCODE_TO_ID.dot)
            } else if (KeyCodes.plus?.some((i) => i.code === keyCode)) {
                onClick('PLUS') // +
                hoverKeyPress(MAP_KEYCODE_TO_ID.plus)
            } else if (KeyCodes.minus?.some((i) => i.code === keyCode)) {
                onClick('MINUS') // -
                hoverKeyPress(MAP_KEYCODE_TO_ID.minus)
            } else if (
                KeyCodes.equal?.some((i) => i.code === keyCode) ||
                KeyCodes.enter?.some((i) => i.code === keyCode)
            ) {
                onClick('EQUAL') // =
                hoverKeyPress(MAP_KEYCODE_TO_ID.equal)
            } else if (
                KeyCodes.numeric?.some((i) => i.code === keyCode) &&
                isNumber(parseInt(key))
            ) {
                onClick(parseInt(key))
                hoverKeyPress(parseInt(key))
            }
        },
        [onClick, hoverKeyPress],
    )

    useEffect(() => {
        if (!disabled && !disableKeyboard) {
            window.addEventListener('keydown', handleKeyPress)
            return () => {
                window.removeEventListener('keydown', handleKeyPress)
            }
        }
    }, [handleKeyPress, disabled, disableKeyboard])

    return (
        <Grid height={'100%'} container>
            {!hideQuickButtons && (
                <Grid
                    container
                    item
                    xs={isMobile ? 12 : 3}
                    height={isMobile ? '20%' : '100%'}
                >
                    <Grid container item xs={isMobile ? 3 : 12}>
                        <PanelBtn
                            label={'5%'}
                            value={'5%'}
                            onClick={() => onClick('5%', true)}
                            id={MAP_KEYCODE_TO_ID.percen5}
                            isMobile={isMobile}
                            disabled={disabled || !isPercentage}
                        />
                    </Grid>
                    <Grid container item xs={isMobile ? 3 : 12}>
                        <PanelBtn
                            label={'10%'}
                            value={'10%'}
                            id={MAP_KEYCODE_TO_ID.percen10}
                            onClick={() => onClick('10%', true)}
                            isMobile={isMobile}
                            disabled={disabled || !isPercentage}
                        />
                    </Grid>
                    <Grid container item xs={isMobile ? 3 : 12}>
                        <PanelBtn
                            label={'15%'}
                            value={'15%'}
                            id={MAP_KEYCODE_TO_ID.percen15}
                            onClick={() => onClick('15%', true)}
                            isMobile={isMobile}
                            disabled={disabled || !isPercentage}
                        />
                    </Grid>
                    <Grid container item xs={isMobile ? 3 : 12}>
                        <PanelBtn
                            label={'20%'}
                            onClick={() => onClick('20%', true)}
                            value={'20%'}
                            id={MAP_KEYCODE_TO_ID.percen20}
                            isMobile={isMobile}
                            disabled={disabled || !isPercentage}
                        />
                    </Grid>
                </Grid>
            )}
            <Grid
                item
                container
                xs={isMobile || hideQuickButtons ? 12 : 9}
                height={isMobile && !hideQuickButtons ? '80%' : '100%'}
            >
                <Grid container item xs={9}>
                    {numbers.map((i: number, k) => (
                        <Grid container key={`numpad-${k}`} item xs={4}>
                            <PanelBtn
                                label={i}
                                value={i}
                                onClick={() => onClick(i)}
                                id={`numpad${i}`}
                                isMobile={isMobile}
                                disabled={disabled}
                            />
                        </Grid>
                    ))}
                    <Grid item container xs={12} justifyContent={'center'}>
                        <Grid
                            container
                            item
                            xs={
                                // enable other buttons or have only 0 button
                                // currently, '00' is used with '0' and 'DOT' only (no in calculator mode)
                                (!hideDot &&
                                    (isCalculatorMode ||
                                        isDoubleZeroEnabled)) ||
                                !(!hideDot || isCalculatorMode)
                                    ? 4
                                    : 8
                            }
                        >
                            <PanelBtn
                                label={0}
                                value={0}
                                onClick={() => onClick(0)}
                                id={MAP_KEYCODE_TO_ID.numpad0}
                                isMobile={isMobile}
                                disabled={disabled}
                            />
                        </Grid>
                        {isDoubleZeroEnabled && (
                            <Grid container item xs={4}>
                                <PanelBtn
                                    label={'00'}
                                    value={'00'}
                                    onClick={() => onClick('00')}
                                    id={MAP_KEYCODE_TO_ID.numpad00}
                                    isMobile={isMobile}
                                    disabled={disabled}
                                />
                            </Grid>
                        )}
                        {!hideDot && (
                            <Grid container item xs={4}>
                                <PanelBtn
                                    label={'.'}
                                    value={'DOT'}
                                    onClick={() => onClick('DOT')}
                                    id={MAP_KEYCODE_TO_ID.dot}
                                    isMobile={isMobile}
                                    disabled={disabled}
                                />
                            </Grid>
                        )}
                        {isCalculatorMode && (
                            <Grid container item xs={4}>
                                <PanelBtn
                                    label={'='}
                                    value={'EQUAL'}
                                    onClick={() => onClick('EQUAL')}
                                    id={MAP_KEYCODE_TO_ID.equal}
                                    isMobile={isMobile}
                                    disabled={disabled}
                                />
                            </Grid>
                        )}
                    </Grid>
                </Grid>
                <Grid container item xs={3} direction={'column'}>
                    {onSwitchMode && (
                        <Grid container item xs>
                            <PanelBtn
                                label={isPercentage ? 'CUSTOM' : '%'}
                                value={''}
                                onClick={() =>
                                    onSwitchMode(
                                        isPercentage
                                            ? CalculatorSwitchMode.Custom
                                            : CalculatorSwitchMode.Percentage,
                                    )
                                }
                                id=""
                                isMobile={isMobile}
                                disabled={disabled || disableSwitchMode}
                            />
                        </Grid>
                    )}
                    <Grid container item xs>
                        <PanelBtn
                            label={'CLEAR'}
                            value={'CLEAR'}
                            onClick={() => onClick('CLEAR')}
                            icon={<Close />}
                            id={MAP_KEYCODE_TO_ID.delete}
                            isMobile={isMobile}
                            disabled={disabled}
                        />
                    </Grid>
                    {isCalculatorMode && (
                        <>
                            <Grid container item xs>
                                <PanelBtn
                                    label={'+'}
                                    value={'PLUS'}
                                    onClick={() => onClick('PLUS')}
                                    id={MAP_KEYCODE_TO_ID.plus}
                                    isMobile={isMobile}
                                    disabled={disabled}
                                />
                            </Grid>
                            <Grid container item xs>
                                <PanelBtn
                                    label={'-'}
                                    value={'MINUS'}
                                    onClick={() => onClick('MINUS')}
                                    id={MAP_KEYCODE_TO_ID.minus}
                                    isMobile={isMobile}
                                    disabled={disabled}
                                />
                            </Grid>
                        </>
                    )}
                    <Grid container item xs>
                        <PanelBtn
                            label={'BACK'}
                            onClick={() => onClick('BACK')}
                            icon={<Backspace />}
                            id={MAP_KEYCODE_TO_ID.back}
                            isMobile={isMobile}
                            disabled={disabled}
                        />
                    </Grid>
                </Grid>
            </Grid>
        </Grid>
    )
}

export default NumberPad
